import React from 'react';
import type { WidgetPreChatField } from '../../types/inboxConfiguration';
import { DEFAULT_LAUNCHER_LABEL, DEFAULT_PRE_CHAT_MESSAGE } from '../../types/inboxConfiguration';

export interface WidgetLivePreviewProps {
  headerTitle?: string;
  welcomeMessage?: string;
  launcherLabel?: string;
  primaryColor?: string;
  position?: string;
  voiceEnabled?: boolean;
  preChatFormEnabled?: boolean;
  preChatMessage?: string;
  preChatFields?: WidgetPreChatField[];
  avatarUrl?: string;
  presenceLabel?: string;
  hoursNotice?: { headline: string; detail: string };
  className?: string;
}

export const WidgetLivePreview: React.FC<WidgetLivePreviewProps> = ({
  headerTitle = 'Atendimento',
  welcomeMessage = 'Olá! Como podemos ajudar?',
  launcherLabel = DEFAULT_LAUNCHER_LABEL,
  primaryColor = '#2563eb',
  position = 'right',
  voiceEnabled = true,
  preChatFormEnabled = false,
  preChatMessage = DEFAULT_PRE_CHAT_MESSAGE,
  preChatFields = [],
  avatarUrl,
  presenceLabel,
  hoursNotice,
  className = '',
}) => {
  const isLeft = position === 'left';
  const enabledFields = preChatFields.filter(field => field.enabled);
  const teaserText = launcherLabel.trim();

  return (
    <div
      className={`overflow-hidden rounded-2xl border border-slate-700/40 shadow-xl ${className}`}
    >
      <div className="border-b border-slate-600/50 bg-slate-800 px-4 py-2.5">
        <div className="flex items-center gap-1.5 mb-2">
          <span className="h-2.5 w-2.5 rounded-full bg-slate-500" aria-hidden />
          <span className="h-2.5 w-2.5 rounded-full bg-slate-500" aria-hidden />
          <span className="h-2.5 w-2.5 rounded-full bg-slate-500" aria-hidden />
        </div>
        <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-slate-400">
          Prévia do widget
        </p>
      </div>

      <div
        className="bg-gradient-to-br from-slate-700 via-slate-600 to-slate-800 p-5"
      >
        <div
          className={`relative mx-auto flex min-h-[540px] w-full max-w-[380px] flex-col ${
            isLeft ? 'items-start' : 'items-end'
          }`}
        >
          <div
            className="flex w-full max-w-[340px] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/10"
            style={{ height: 500 }}
          >
            {/* ── Header com gradiente ── */}
            <div
              className="relative flex items-center justify-between gap-2.5 px-4 py-3.5 text-white"
              style={{
                background: `linear-gradient(135deg, ${primaryColor} 0%, color-mix(in srgb, ${primaryColor} 82%, #000) 100%)`,
              }}
            >
              <div
                className="pointer-events-none absolute inset-0"
                style={{
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.08) 0%, transparent 100%)',
                }}
              />
              <div className="relative z-10 flex min-w-0 flex-1 items-center gap-2.5">
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt=""
                    className="h-9 w-9 flex-shrink-0 rounded-full object-cover ring-2 ring-white/20"
                    style={{ background: 'rgba(255,255,255,0.15)' }}
                  />
                ) : null}
                <div className="min-w-0 flex-1">
                  <span className="block truncate text-[15px] font-semibold tracking-tight">
                    {headerTitle || 'Atendimento'}
                  </span>
                  {presenceLabel ? (
                    <span className="block truncate text-xs font-medium text-white/85">
                      {presenceLabel}
                    </span>
                  ) : null}
                </div>
              </div>
              <span
                className="relative z-10 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-lg leading-none transition-colors hover:bg-white/[0.18]"
                aria-hidden
              >
                −
              </span>
            </div>

            {hoursNotice ? (
              <div className="border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-amber-900">
                <p className="text-xs font-semibold">{hoursNotice.headline}</p>
                <p className="mt-0.5 text-[11px] leading-snug opacity-90">{hoursNotice.detail}</p>
              </div>
            ) : null}

            {preChatFormEnabled ? (
              <div className="flex flex-1 flex-col gap-3.5 overflow-y-auto bg-slate-50 p-5">
                <p className="text-sm leading-relaxed text-slate-600 whitespace-pre-wrap">
                  {preChatMessage || DEFAULT_PRE_CHAT_MESSAGE}
                </p>
                <div className="space-y-3.5">
                  {enabledFields.map(field => (
                    <label key={field.name} className="block space-y-1.5">
                      <span className="text-xs font-medium text-slate-500">
                        {field.label}
                        {field.required ? ' *' : ''}
                      </span>
                      <input
                        type="text"
                        disabled
                        placeholder={field.placeholder || field.label}
                        className="w-full rounded-[10px] border-[1.5px] border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-400"
                      />
                    </label>
                  ))}
                  {enabledFields.length === 0 ? (
                    <p className="text-xs text-slate-400">Nenhum campo habilitado</p>
                  ) : null}
                </div>
                <button
                  type="button"
                  disabled
                  className="mt-auto rounded-[10px] px-4 py-3 text-sm font-semibold text-white shadow-md opacity-90 transition-transform hover:-translate-y-px"
                  style={{
                    backgroundColor: primaryColor,
                    boxShadow: `0 2px 8px ${primaryColor}40`,
                  }}
                >
                  Começar atendimento
                </button>
              </div>
            ) : (
              <>
                {/* ── Messages area ── */}
                <div className="flex flex-1 flex-col gap-2 overflow-y-auto bg-slate-50 p-4">
                  <div
                    className="max-w-[82%] rounded-[18px] rounded-bl-[6px] bg-white px-3.5 py-2.5 text-sm leading-relaxed text-slate-700 ring-1 ring-slate-200/60"
                    style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 2px 6px rgba(0,0,0,0.03)' }}
                  >
                    {welcomeMessage || 'Olá! Como podemos ajudar?'}
                  </div>
                </div>

                {/* ── Input area ── */}
                <div className="flex items-center gap-2 border-t border-slate-100 bg-white p-2.5">
                  {voiceEnabled ? (
                    <button
                      type="button"
                      disabled
                      aria-label="Microfone"
                      title="Microfone"
                      className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border-[1.5px] border-slate-200 bg-slate-50 text-slate-400 transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        className="h-4 w-4"
                        aria-hidden
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
                        />
                      </svg>
                    </button>
                  ) : null}
                  <input
                    type="text"
                    disabled
                    placeholder="Digite sua mensagem..."
                    className="flex-1 rounded-full border-[1.5px] border-slate-200 bg-slate-50 px-3.5 py-2 text-sm text-slate-400 placeholder-slate-400"
                  />
                  <button
                    type="button"
                    disabled
                    aria-label="Enviar mensagem"
                    title="Enviar"
                    className="inline-flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full text-white shadow-md transition-transform hover:scale-105"
                    style={{
                      backgroundColor: primaryColor,
                      boxShadow: `0 2px 8px ${primaryColor}4D`,
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="h-[18px] w-[18px]"
                      aria-hidden
                    >
                      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                    </svg>
                  </button>
                </div>
              </>
            )}
          </div>

          <div className={`mt-3.5 flex items-end gap-3 ${isLeft ? 'flex-row-reverse' : ''}`}>
            {teaserText ? (
              <div className="mb-1.5 flex max-w-[200px] items-start gap-2 rounded-2xl bg-white px-3 py-2.5 text-sm leading-snug text-slate-900 shadow-lg">
                <p className="m-0 flex-1">{teaserText}</p>
                <span className="text-slate-400" aria-hidden>×</span>
              </div>
            ) : null}
          {/* ── Launcher FAB ── */}
          <button
            type="button"
            disabled
            aria-label="Fechar atendimento"
            className="flex h-[60px] w-[60px] shrink-0 items-center justify-center overflow-hidden rounded-full text-white shadow-xl transition-transform hover:scale-105"
            style={{
              backgroundColor: primaryColor,
              boxShadow: `0 4px 12px ${primaryColor}59, 0 1px 3px rgba(0,0,0,0.1)`,
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.25}
              className="h-[26px] w-[26px]"
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          </div>
        </div>

        <p className="mt-3 text-center text-[11px] text-slate-300">
          Posição: {isLeft ? 'Esquerda' : 'Direita'}
        </p>
      </div>
    </div>
  );
};
